import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { CalendarDays, ChevronRight, Hand, Sparkles, Ticket } from 'lucide-react'
import { PillTabs } from '@/components/PillTabs'
import { ErrorState } from '@/components/ErrorState'
import { EmptyState } from '@/components/EmptyState'
import { SkeletonCard } from '@/components/LoadingSkeleton'
import { FreshnessBanner, isActive } from '@/components/quiniela/FreshnessBanner'
import { MatchProbRow } from '@/components/quiniela/MatchProbRow'
import { PlenoCard } from '@/components/quiniela/PlenoCard'
import { BudgetPanel } from '@/components/quiniela/BudgetPanel'
import { TicketBuilder } from '@/components/quiniela/TicketBuilder'
import { useBudgetProposal, useQuinielaCurrent, useStartAnalysis } from '@/hooks/useQuiniela'
import { formatKickoff } from '@/lib/utils'

type Tab = 'partidos' | 'ja' | 'manual'

function drawLabel(iso: string): string {
  const label = new Date(`${iso}T12:00:00`).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })
  return label.charAt(0).toUpperCase() + label.slice(1)
}

export function QuinielaPage() {
  const { data, isLoading, isError, error, refetch } = useQuinielaCurrent(true)
  const start = useStartAnalysis()
  const [tab, setTab] = useState<Tab>('partidos')
  const autoStarted = useRef(false)
  const jornadaId = data?.jornada?.id
  const proposal = useBudgetProposal(jornadaId)

  const noSnapshot = !!data?.available && !data.snapshot
  const analysing = isActive(data?.analysis)
  const lastFailed = data?.analysis?.status === 'failed'

  // Jornada publicada y sin análisis: se analiza de inmediato (no se espera a ninguna fecha).
  useEffect(() => {
    if (jornadaId && noSnapshot && !analysing && !lastFailed && !autoStarted.current && !start.isPending) {
      autoStarted.current = true
      start.mutate({ jornadaId, force: false })
    }
  }, [jornadaId, noSnapshot, analysing, lastFailed, start])

  if (isLoading) {
    return <div className="p-4 space-y-3"><SkeletonCard /><SkeletonCard /><SkeletonCard /></div>
  }
  if (isError || !data) {
    return <div className="p-4"><ErrorState title="No pudimos cargar La Quiniela" error={error} onRetry={() => refetch()} /></div>
  }
  if (!data.available || !data.jornada) {
    return (
      <div className="p-4">
        <EmptyState
          icon={Ticket} title="La jornada todavía no está disponible"
          body="En cuanto se publique la próxima jornada oficial, JA la analizará aquí con sus 15 partidos."
        />
      </div>
    )
  }

  const { jornada } = data
  const snap = data.snapshot
  const matches = snap?.matches ?? []
  const regular = matches.filter((m) => m.position <= 14)
  const pleno = matches.find((m) => m.position === 15)
  const startAnalysis = (force: boolean) => start.mutate({ jornadaId: jornada.id, force })

  return (
    <div className="p-4 pb-8 space-y-3.5 max-w-2xl md:max-w-5xl mx-auto">
      <header className="card-premium-glow !p-4 space-y-2.5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="eyebrow text-brand">La Quiniela</p>
            <h1 className="font-display text-2xl font-bold text-text leading-none mt-1.5">Jornada {jornada.number}</h1>
          </div>
          <span className="h-10 w-10 rounded-xl bg-brand-soft/60 border border-brand/30 flex items-center justify-center shrink-0">
            <Ticket className="h-5 w-5 text-brand" />
          </span>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-text-muted">
          <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-text-dim" /> <span>{drawLabel(jornada.draw_date)}</span></span>
          {jornada.first_kickoff_utc && <span>Primer partido: <span className="figure text-text">{formatKickoff(jornada.first_kickoff_utc)}</span></span>}
        </div>
      </header>

      {(data.my_tickets?.length ?? 0) > 0 && (
        <Link to="/mas/historial?tab=apuestas&sub=boletos" className="flex items-center justify-between rounded-xl border border-brand/30 bg-brand-soft/20 px-3.5 py-2.5 text-[12px]">
          <span className="text-text-muted">Has jugado <span className="figure font-bold text-brand">{data.my_tickets!.length}</span> {data.my_tickets!.length === 1 ? 'boleto' : 'boletos'} esta jornada</span>
          <span className="font-bold text-brand">Ver Mis boletos</span>
        </Link>
      )}

      <FreshnessBanner current={data} onUpdate={() => startAnalysis(!noSnapshot)} updating={start.isPending} starting={start.isPending && noSnapshot} />

      {snap && (
        <>
          <PillTabs
            items={[
              { key: 'partidos', label: 'Partidos' },
              { key: 'ja', label: 'Créala con JA' },
              { key: 'manual', label: 'Créala tú mismo' },
            ]}
            active={tab} onChange={(k) => setTab(k as Tab)}
          />

          {tab === 'partidos' && (
            <div className="flex flex-col gap-3.5 md:grid md:grid-cols-[minmax(0,1fr)_22rem] md:items-start md:gap-5">
              <section className="card order-2 md:order-none">
                <div className="pb-1">
                  <p className="eyebrow">Probabilidad de cada signo</p>
                  <p className="text-[11px] text-text-dim mt-1">1 = gana el local · X = empate · 2 = gana el visitante</p>
                </div>
                {regular.map((m) => <MatchProbRow key={m.position} match={m} />)}
                <p className="text-[10px] text-text-dim leading-relaxed pt-2">
                  El signo en verde es el más probable según JA. La barra reparte la probabilidad entre 1, X y 2: cuanto más
                  repartida, más abierto el partido y más probable un doble o triple.
                </p>
              </section>

              <div className="contents md:block md:space-y-3.5 md:sticky md:top-20">
                <div className="order-1 grid gap-2.5 sm:grid-cols-2 md:grid-cols-1">
                  <button type="button" onClick={() => setTab('ja')} className="card-premium-glow !p-3.5 text-left flex items-center gap-3 active-scale">
                    <span className="h-9 w-9 rounded-xl bg-brand-soft/60 border border-brand/30 flex items-center justify-center shrink-0"><Sparkles className="h-5 w-5 text-brand" /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-bold text-text">Créala con John Anthony</span>
                      <span className="block text-[11px] text-text-muted leading-snug">Tu presupuesto, la mejor Quiniela posible.</span>
                    </span>
                    <ChevronRight className="h-4 w-4 text-text-dim shrink-0" />
                  </button>
                  <button type="button" onClick={() => setTab('manual')} className="card !p-3.5 text-left flex items-center gap-3 active-scale hover:border-text-dim/40">
                    <span className="h-9 w-9 rounded-xl bg-panel-elevated border border-border flex items-center justify-center shrink-0"><Hand className="h-5 w-5 text-text-muted" /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-bold text-text">Créala tú mismo</span>
                      <span className="block text-[11px] text-text-muted leading-snug">Elige tus signos; JA calcula coste y probabilidad.</span>
                    </span>
                    <ChevronRight className="h-4 w-4 text-text-dim shrink-0" />
                  </button>
                </div>
                <div className="order-3"><PlenoCard match={pleno} pleno={snap.pleno} /></div>
              </div>
            </div>
          )}

          {tab === 'ja' && (
            <BudgetPanel
              current={data} matches={matches} state={proposal.state}
              onGenerate={(b) => { void proposal.request(b) }}
              onAcceptCurrent={(b) => { void proposal.request(b, true) }}
              onReset={proposal.reset}
            />
          )}

          {tab === 'manual' && <TicketBuilder current={data} matches={matches} />}
        </>
      )}
    </div>
  )
}
